import { sheetWidthPolicy } from './sheetWidthPolicy'

const INCH_UNITS = 10_000
const SQUARE_INCHES_PER_SQUARE_FOOT = 144
const GEOMETRY_EPSILON_INCHES = 0.0001
const TRIGONOMETRY_EPSILON = 1e-12

export const DYNAMIC_BOTTOM_SAFETY_MARGIN_INCHES = 0.125

function toInchUnits(value) {
  return Math.round(Number(value) * INCH_UNITS)
}

function roundUpUnits(valueUnits, incrementUnits) {
  return Math.ceil(valueUnits / incrementUnits) * incrementUnits
}

function normalizeTrigonometricValue(value) {
  if (Math.abs(value) < TRIGONOMETRY_EPSILON) return 0
  if (Math.abs(1 - Math.abs(value)) < TRIGONOMETRY_EPSILON) return Math.sign(value)

  return value
}

function roundedCents(widthUnits, heightUnits, rateCents) {
  const numerator = BigInt(widthUnits) * BigInt(heightUnits) * BigInt(rateCents)
  const denominator = BigInt(INCH_UNITS) * BigInt(INCH_UNITS) * BigInt(SQUARE_INCHES_PER_SQUARE_FOOT)

  return Number((numerator + (denominator / 2n)) / denominator)
}

export function rotatedItemBounds(item) {
  const x = Number(item.x_in)
  const y = Number(item.y_in)
  const width = Number(item.width_in)
  const height = Number(item.height_in)
  const rotation = Number(item.rotation_deg || 0)
  const radians = rotation * Math.PI / 180
  const sine = normalizeTrigonometricValue(Math.sin(radians))
  const cosine = normalizeTrigonometricValue(Math.cos(radians))
  const rotatedWidth = (width * Math.abs(cosine)) + (height * Math.abs(sine))
  const rotatedHeight = (width * Math.abs(sine)) + (height * Math.abs(cosine))
  // Fabric serializes left/top using the object's rotated left/top origin.
  // Rotate the half-size vector to recover the visual center before deriving the AABB.
  const centerX = x + ((width / 2) * cosine) - ((height / 2) * sine)
  const centerY = y + ((width / 2) * sine) + ((height / 2) * cosine)

  return {
    left: centerX - (rotatedWidth / 2),
    right: centerX + (rotatedWidth / 2),
    top: centerY - (rotatedHeight / 2),
    bottom: centerY + (rotatedHeight / 2),
  }
}

export function calculateDynamicPricing(layout, settings) {
  if (settings?.pricing_mode !== 'per_square_foot') return null

  const widthUnits = toInchUnits(settings.width_inches)
  const layoutWidthInches = sheetWidthPolicy(settings.width_inches).layoutWidthIn
  const minimumUnits = toInchUnits(settings.min_height_inches)
  const maximumUnits = toInchUnits(settings.max_height_inches)
  const incrementUnits = toInchUnits(settings.height_increment_inches)
  const marginUnits = toInchUnits(
    settings.bottom_safety_margin_inches ?? DYNAMIC_BOTTOM_SAFETY_MARGIN_INCHES
  )
  const rateCents = Number.parseInt(settings.price_per_square_foot_cents, 10)
  const billingUnit = settings.billing_unit === 'sheet_variant' ? 'sheet_variant' : 'square_foot'
  const tiers = billingUnit === 'sheet_variant'
    ? [...(settings.tiers || [])].sort((a, b) => a.height_in - b.height_in)
    : null
  const maxSheets = Number.parseInt(settings.max_sheets, 10) || null
  const sheets = layout?.sheets || []
  const items = sheets.flatMap(sheet => sheet.items || [])

  if (!items.length) {
    return {
      hasContent: false,
      widthInches: widthUnits / INCH_UNITS,
      heightInches: minimumUnits / INCH_UNITS,
      totalHeightInches: minimumUnits / INCH_UNITS,
      sheetCount: 0,
      sheetHeightsInches: [],
      squareFeet: null,
      rateCents,
      totalCents: null,
      exceedsMaximum: false,
      hasInvalidBounds: false,
      billingUnit,
      sheets: null,
      tiersVersion: settings.tiers_version ?? null,
      missingTier: null,
      tooManySheets: false,
    }
  }

  let exceedsMaximum = false
  let hasInvalidBounds = false
  const occupiedBottoms = []
  const sheetHeightUnits = []

  sheets.forEach(sheet => {
    const sheetItems = sheet.items || []
    if (!sheetItems.length) return

    const bounds = sheetItems.map(rotatedItemBounds)
    const occupiedBottom = Math.max(...bounds.map(bound => bound.bottom))
    const requiredHeightUnits = Math.ceil((occupiedBottom * INCH_UNITS) + marginUnits)
    const roundedHeightUnits = Math.max(
      minimumUnits,
      roundUpUnits(requiredHeightUnits, incrementUnits)
    )

    occupiedBottoms.push(occupiedBottom)
    sheetHeightUnits.push(Math.min(roundedHeightUnits, maximumUnits))
    exceedsMaximum ||= requiredHeightUnits > maximumUnits || roundedHeightUnits > maximumUnits
    hasInvalidBounds ||= bounds.some(bound => (
      bound.left < -GEOMETRY_EPSILON_INCHES ||
      bound.top < -GEOMETRY_EPSILON_INCHES ||
      bound.right > layoutWidthInches + GEOMETRY_EPSILON_INCHES
    ))
  })

  // `sheets` is already the layout's sheet list in this scope; the priced
  // lines get their own name.
  let missingTier = null
  let quotedSheets = null
  if (tiers) {
    quotedSheets = []
    sheetHeightUnits.forEach((heightUnits, index) => {
      const tier = tiers.find(row => toInchUnits(row.height_in) >= heightUnits)
      if (!tier) {
        if (missingTier === null) missingTier = index + 1
        return
      }
      sheetHeightUnits[index] = toInchUnits(tier.height_in)
      quotedSheets.push({ sheet: index + 1, heightIn: tier.height_in, priceCents: tier.price_cents })
    })
  }
  // The MAX_VARIANT_SHEETS cap only exists server-side for the variant-sheet
  // tier table; a square-foot vendor has no such limit.
  const tooManySheets = Boolean(tiers) && Boolean(maxSheets) && sheetHeightUnits.length > maxSheets

  const totalHeightUnits = sheetHeightUnits.reduce((sum, height) => sum + height, 0)
  const squareFeet = (widthUnits * totalHeightUnits) /
    (INCH_UNITS * INCH_UNITS * SQUARE_INCHES_PER_SQUARE_FOOT)
  const totalCents = tiers
    ? (missingTier || tooManySheets ? null : quotedSheets.reduce((sum, sheet) => sum + sheet.priceCents, 0))
    : roundedCents(widthUnits, totalHeightUnits, rateCents)

  return {
    hasContent: true,
    widthInches: widthUnits / INCH_UNITS,
    heightInches: totalHeightUnits / INCH_UNITS,
    totalHeightInches: totalHeightUnits / INCH_UNITS,
    sheetCount: sheetHeightUnits.length,
    sheetHeightsInches: sheetHeightUnits.map(height => height / INCH_UNITS),
    occupiedBottomInches: Math.max(...occupiedBottoms),
    squareFeet,
    rateCents,
    totalCents,
    billingUnit,
    sheets: quotedSheets,
    tiersVersion: settings.tiers_version ?? null,
    missingTier,
    tooManySheets,
    exceedsMaximum: exceedsMaximum || missingTier !== null,
    hasInvalidBounds,
  }
}
